import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { formatClock, type InterviewSummaryView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ToneIcon, toneCardClass, type Tone } from '../../../components/Tone';
import { formatClockTime, formatIsoDate, formatZoneName } from '../../../lib/format';
import { useNow } from '../../../lib/use-now';
import { useCanView } from '../../../stores/session';
import { useInterviews } from '../api/interviews-api';
import { InterviewRow } from '../components/InterviewRow';
import { InterviewsHeader } from '../components/InterviewsHeader';
import type { InterviewListContext } from '../interview-list-context';
import { useViewerZone } from '../hooks/use-interview-actions';
import { dayHeading, dayKey, shortDay } from '../interview-ui';
import styles from '../components/Interviews.module.css';

const DAY_MS = 86_400_000;
/** The grid's hours: a working day with an hour's room either side. */
const FIRST_HOUR = 8;
const LAST_HOUR = 20;
const WORKING_WEEK = 5;
const WEEK = 7;
/** A booked interview inside this window reads as due soon (ADR 0035's "within 24 hours"). */
const SOON_MS = DAY_MS;

/** `YYYY-MM-DD` plus days, on the calendar string itself (no timezone involved). */
function addDays(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** The Sunday on or before a day — the D14 week starts on Sunday. */
function sundayOf(day: string): string {
  return addDays(day, -new Date(`${day}T00:00:00Z`).getUTCDay());
}

/** Hours after midnight in the viewer's zone, as a fraction — where a block sits in its column. */
function hoursInto(iso: string, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date(iso));
  const value = (type: string): number => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return (value('hour') % 24) + value('minute') / 60;
}

/**
 * SCH-02 — the week's interviews for everyone the viewer can see (S19 Q7): a Sun–Thu grid
 * with time down the side on a computer, the same week as a list by day on a phone. The
 * weekend columns appear only when something is booked on them.
 */
export function InterviewCalendarPage(): ReactNode {
  const canView = useCanView('interview');
  const canSchedule = useCanView('job_config');
  const navigate = useNavigate();
  const zone = useViewerZone();
  const now = useNow();
  const today = dayKey(new Date(now).toISOString(), zone);
  const [weekStart, setWeekStart] = useState(() => sundayOf(today));
  // A day either side, so every interview in the viewer's week arrives whatever their zone.
  const from = new Date(Date.parse(`${weekStart}T00:00:00Z`) - DAY_MS).toISOString();
  const to = new Date(Date.parse(`${weekStart}T00:00:00Z`) + (WEEK + 1) * DAY_MS).toISOString();
  const filter = { view: 'calendar', from, to } as const;
  const query = useInterviews(filter);

  if (!canView) return <ForbiddenState resource="interviews" contact="Your admin" />;

  const days = Array.from({ length: WEEK }, (_, index) => addDays(weekStart, index));
  const rows = (query.data ?? []).filter((row) => row.startsAt && days.includes(dayKey(row.startsAt, zone)));
  const isWeekendUsed = rows.some((row) => row.startsAt && days.slice(WORKING_WEEK).includes(dayKey(row.startsAt, zone)));
  const shownDays = isWeekendUsed ? days : days.slice(0, WORKING_WEEK);
  const onDay = (day: string): readonly InterviewSummaryView[] => rows.filter((row) => row.startsAt && dayKey(row.startsAt, zone) === day);
  // Geometry, not appearance: the number of columns the grid draws.
  const isThisWeek = weekStart === sundayOf(today);
  const listContext: InterviewListContext = { filter, label: isThisWeek ? 'this week' : `in the week of ${formatIsoDate(weekStart)}`, days };
  const gridStyle: CSSProperties & Readonly<Record<'--nr-week-days', number>> = { '--nr-week-days': shownDays.length };

  return (
    <div className={styles.screen}>
      <InterviewsHeader current="calendar" />
      <Card
        title="Interview calendar"
        meta={`Times in ${formatZoneName(now, { timeZone: zone })}`}
        action={
          <div className={styles.weekNav}>
            <Button variant="ghost" size="sm" icon={ChevronLeft} aria-label="Previous week" onClick={() => setWeekStart(addDays(weekStart, -WEEK))} />
            <span className={styles.weekLabel}>
              {formatIsoDate(weekStart)} – {formatIsoDate(addDays(weekStart, WEEK - 1))}
            </span>
            <Button variant="ghost" size="sm" icon={ChevronRight} aria-label="Next week" onClick={() => setWeekStart(addDays(weekStart, WEEK))} />
            <Button variant="outline" size="sm" disabled={isThisWeek} onClick={() => setWeekStart(sundayOf(today))}>
              This week
            </Button>
          </div>
        }
      >
        {query.isPending ? (
          <SkeletonLines count={8} />
        ) : query.error ? (
          <ServerErrorState onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            isInset
            title="No interviews this week"
            body="Interviews appear here once a time is booked. Those still waiting for a time are under To schedule."
            action={
              canSchedule ? (
                <Button variant="solid" size="sm" onClick={() => void navigate({ to: '/interviews/to-schedule' })}>
                  See what's waiting
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => void navigate({ to: '/interviews/availability' })}>
                  Set my availability
                </Button>
              )
            }
          />
        ) : (
          <>
            <div className={styles.week} style={gridStyle} role="grid" aria-label="Interviews this week">
              <div className={styles.weekCorner} />
              {shownDays.map((day) => (
                <div key={day} className={styles.weekHead} role="columnheader">
                  {shortDay(day)}
                </div>
              ))}
              <div className={styles.hours} aria-hidden>
                {Array.from({ length: LAST_HOUR - FIRST_HOUR }, (_, index) => (
                  <span key={index} className={styles.hour}>
                    {formatClock(`${String(FIRST_HOUR + index).padStart(2, '0')}:00`)}
                  </span>
                ))}
              </div>
              {shownDays.map((day) => (
                <div key={day} className={styles.dayColumn} role="gridcell">
                  {onDay(day).map((row) => (
                    <Block key={row.id} interview={row} zone={zone} now={now} listContext={listContext} />
                  ))}
                </div>
              ))}
            </div>
            <div className={styles.dayList}>
              {shownDays
                .filter((day) => onDay(day).length > 0)
                .map((day) => (
                  <section key={day} aria-label={dayHeading(day)}>
                    <h3 className={styles.dayHeading}>{dayHeading(day)}</h3>
                    <ul className={styles.list}>
                      {onDay(day).map((row) => (
                        <InterviewRow key={row.id} interview={row} zone={zone} listContext={listContext} />
                      ))}
                    </ul>
                  </section>
                ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

/** No-shows are overdue, completed is done, cancelled is closed; a booked one is due soon inside 24 hours. */
function blockTone(interview: InterviewSummaryView, now: number): Tone {
  if (interview.status === 'candidate_no_show' || interview.status === 'panelist_no_show') return 'urgent';
  if (interview.status === 'completed') return 'done';
  if (interview.status === 'cancelled') return 'ended';
  if (interview.isStranded) return 'attention';
  const startsIn = interview.startsAt ? Date.parse(interview.startsAt) - now : Number.POSITIVE_INFINITY;
  return startsIn > 0 && startsIn <= SOON_MS ? 'attention' : 'interview';
}

function Block({ interview, zone, now, listContext }: { readonly interview: InterviewSummaryView; readonly zone: string; readonly now: number; readonly listContext: InterviewListContext }): ReactNode {
  if (!interview.startsAt || !interview.endsAt) return null;
  const start = Math.max(hoursInto(interview.startsAt, zone), FIRST_HOUR);
  const end = Math.min(Math.max(hoursInto(interview.endsAt, zone), start + 0.5), LAST_HOUR);
  // Geometry, not appearance: where the block sits in its day, in hour-heights.
  const style: CSSProperties = {
    top: `calc(var(--nr-week-hour-h) * ${start - FIRST_HOUR})`,
    height: `calc(var(--nr-week-hour-h) * ${end - start})`,
  };
  const tone = blockTone(interview, now);
  return (
    <Link to="/interviews/$interviewId" params={{ interviewId: interview.id }} state={{ interviewList: listContext }} className={`${toneCardClass(true)} ${styles.block}`} data-tone={tone} style={style}>
      <span className={styles.blockHead}>
        <ToneIcon tone={tone} size="sm" />
        <span className={styles.blockName}>{interview.candidateName}</span>
      </span>
      <span className={styles.blockLine}>
        {formatClockTime(interview.startsAt, { timeZone: zone })} · {interview.jobTitle}
      </span>
    </Link>
  );
}
